import React, { useState } from 'react'
import { Box, CircularProgress, styled } from '@mui/material'

import DeleteIcon from '@mui/icons-material/Delete'
import SaveIcon from '@mui/icons-material/Save'
import CancelIcon from '@mui/icons-material/Close'
import {
  DataGrid,
  GridActionsCellItem,
  GridRowEditStopReasons,
  GridRowModes,
  GridToolbar,
  useGridApiRef
} from '@mui/x-data-grid'
import { ConfirmationDialog } from 'components'
import { useSnackbar } from 'notistack'
import { updateMaterial } from 'service/materials'

const StyledDataGrip = styled(DataGrid)(() => [
  {
    '& .MuiDataGrid-virtualScrollerContent': {
      'flex-basis': '0 !important'
    }
  }
])
export const MaterialsTable = ({ rows, setRows, isLoading }) => {
  const [rowModesModel, setRowModesModel] = useState({})
  const [materialToDelete, setMaterialToDelete] = useState(undefined)

  const { enqueueSnackbar } = useSnackbar()
  const apiRef = useGridApiRef()

  const COLUMNS = [
    { field: 'MaterialNumber', headerName: 'Material Number', flex: 1, hideable: false, disableColumnMenu: true },
    {
      field: 'PartNumber',
      headerName: 'Part Number',
      flex: 1,
      hideable: false,
      sortable: false,
      disableColumnMenu: true,
      editable: true
    },
    {
      field: 'MaterialDescription',
      headerName: 'Material Description',
      flex: 1,
      sortable: false,
      disableColumnMenu: true,
      editable: true
    },
    {
      field: 'OpenArea',
      headerName: 'Open Area',
      flex: 1,
      sortable: false,
      disableColumnMenu: true,
      editable: true
    },
    {
      field: 'Weight',
      headerName: 'Gross Weight',
      flex: 1,
      sortable: false,
      disableColumnMenu: true,
      editable: true
    },
    {
      field: 'SafetyDepth',
      headerName: 'Safety Depth',
      flex: 1,
      sortable: false,
      disableColumnMenu: true,
      editable: true
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 100,
      cellClassName: 'actions',
      getActions: ({ id }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit
        const isLoading = rowModesModel[id]?.mode === 'loading'

        if (isLoading) {
          return [
            <Box
              key="loading"
              sx={{
                width: '14px'
              }}>
              <CircularProgress
                sx={{
                  width: '14px'
                }}
                size="small"
              />
            </Box>
          ]
        }
        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={<SaveIcon />}
              label="Save"
              sx={{
                color: 'primary.main'
              }}
              key="save"
              onClick={handleSaveClick(id)}
            />,
            <GridActionsCellItem
              icon={<CancelIcon />}
              label="Cancel"
              sx={{
                color: 'primary.main'
              }}
              key="cancel"
              className="textPrimary"
              onClick={handleCancelClick(id)}
              color="inherit"
            />
          ]
        }

        return [
          <GridActionsCellItem
            key="delete"
            icon={<DeleteIcon />}
            sx={{
              color: 'error.main'
            }}
            label="Delete"
            onClick={handleDeleteClick(id)}
            color="inherit"
          />
        ]
      }
    }
  ]

  const handleRowEditStop = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true
    }
  }

  const handleSaveClick = (id) => () => {
    apiRef.current.stopRowEditMode({
      id,
      ignoreModifications: false
    })
  }

  const handleDeleteClick = (id) => () => {
    setMaterialToDelete(id)
  }

  const handleCancelClick = (id) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true }
    })
    apiRef.current.stopRowEditMode({
      id,
      ignoreModifications: true
    })
    const editedRow = rows.find((row) => row.MaterialNumber === id)
    if (editedRow.isNew) {
      setRows(rows.filter((row) => row.MaterialNumber !== id))
    }
  }

  const processRowUpdate = async (newRow, oldRow) => {
    const isEqual = JSON.stringify(newRow) === JSON.stringify(oldRow)
    if (isEqual) {
      return oldRow
    }
    try {
      const updatedRow = { ...newRow, isNew: false }
      setRows(rows.map((row) => (row.MaterialNumber === newRow.MaterialNumber ? updatedRow : row)))

      setRowModesModel({ ...rowModesModel, [newRow.MaterialNumber]: { mode: 'loading' } })
      await updateMaterial(newRow.MaterialNumber, newRow)
      enqueueSnackbar({
        message: `Material '${newRow.MaterialNumber}' has been successfully updated`,
        variant: 'success'
      })
      return updatedRow
    } catch {
      enqueueSnackbar({
        message: `An error occurred while updating'${newRow.MaterialNumber}'`,
        variant: 'error'
      })
      apiRef.current.stopRowEditMode({
        id: newRow.MaterialNumber,
        ignoreModifications: true
      })
    } finally {
      setRowModesModel({ ...rowModesModel, [newRow.MaterialNumber]: { mode: GridRowModes.View } })
    }
    return oldRow
  }

  const handleRowModesModelChange = (newRowModesModel) => {
    setRowModesModel(newRowModesModel)
  }

  return (
    <Box height="calc(100% - 235px)">
      <StyledDataGrip
        apiRef={apiRef}
        loading={isLoading}
        showCellVerticalBorder={false}
        getRowId={(data) => {
          return data.MaterialNumber
        }}
        disableMultipleRowSelection
        onRowModesModelChange={handleRowModesModelChange}
        onRowEditStop={handleRowEditStop}
        rows={rows}
        editMode="row"
        columns={COLUMNS}
        slots={{ toolbar: GridToolbar }}
        slotProps={{
          toolbar: {
            showQuickFilter: true,
            csvOptions: {
              allColumns: true
            }
          },
          loadingOverlay: {
            variant: 'skeleton',
            noRowsVariant: 'skeleton'
          }
        }}
        processRowUpdate={processRowUpdate}
      />
      {materialToDelete && (
        <ConfirmationDialog
          saveButtonProps={{
            color: 'error'
          }}
          text={`Are you sure you want to delete '${materialToDelete}'?`}
          onClose={(result) => {
            if (result === true) {
              setRowModesModel((rowModesModel) => {
                return {
                  ...rowModesModel,
                  [materialToDelete]: {
                    mode: 'loading'
                  }
                }
              })

              //setRows(rows.filter((row) => row.MaterialNumber !== materialToDelete))
              enqueueSnackbar({
                message: `Material '${materialToDelete}' has been deleted`
              })
            }
            setMaterialToDelete(undefined)
          }}
        />
      )}
    </Box>
  )
}
